import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import SiteHeader from "../components/SiteHeader";

export default function States() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState(""); // "", "1", "0"

  const [form, setForm] = useState({
    id: null,
    name: "",
    is_active: 1,
  });

  const isEditing = form.id !== null;

  const pickItems = (data) => {
    // Supports both shapes: { items: [...] } OR [...]
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.items)) return data.items;
    return [];
  };

  const loadData = async (opts = {}) => {
    try {
      setLoading(true);
      const q = opts.search !== undefined ? opts.search : search;
      const active = opts.active !== undefined ? opts.active : activeFilter;

      const { data } = await api.get("/api/admin/states", {
        params: {
          q: q || undefined,
          active: active === "" ? undefined : active,
        },
      });

      setItems(pickItems(data));
      setErr("");
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load states");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm({ id: null, name: "", is_active: 1 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      alert("State name is required");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        name: form.name.trim(),
        is_active: form.is_active ? 1 : 0,
      };

      if (isEditing) {
        await api.put(`/api/admin/states/${form.id}`, payload);
      } else {
        await api.post("/api/admin/states", payload);
      }

      resetForm();
      await loadData();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Failed to save state");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setForm({
      id: item.id,
      name: item.name || "",
      is_active: item.is_active === 0 ? 0 : 1,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this state?")) return;
    try {
      setLoading(true);
      await api.delete(`/api/admin/states/${id}`);
      await loadData();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Failed to delete state");
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    page: { minHeight: "100vh", background: "#f1f5f9" },
    container: { maxWidth: 950, margin: "0 auto", padding: 16 },
    card: {
      background: "#fff",
      borderRadius: 10,
      padding: 16,
      boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
      marginBottom: 16,
    },
    input: {
      width: "100%",
      padding: "10px 12px",
      borderRadius: 8,
      border: "1px solid #cbd5e1",
      fontSize: 14,
    },
    label: { fontSize: 13, fontWeight: 600, marginBottom: 4, display: "block" },
    btnPrimary: {
      background: "#0f766e",
      color: "#fff",
      border: "none",
      borderRadius: 999,
      padding: "8px 18px",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
    },
    btnGhost: {
      background: "#e2e8f0",
      borderRadius: 999,
      padding: "8px 16px",
      border: "none",
      fontSize: 14,
      cursor: "pointer",
    },
    pill: (active) => ({
      display: "inline-block",
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      background: active ? "#dcfce7" : "#fee2e2",
      color: active ? "#166534" : "#991b1b",
      border: `1px solid ${active ? "#bbf7d0" : "#fecaca"}`,
    }),
  };

  return (
    <div style={styles.page}>
      <SiteHeader showAdmin />

      <div style={styles.container}>
        {/* Header row */}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
          <h2 style={{ fontSize: 20, margin: 0 }}>State Master</h2>
          <button onClick={() => navigate("/dashboard")} style={styles.btnGhost}>
            ← Back
          </button>
        </div>

        {/* FORM CARD */}
        <div style={styles.card}>
          <form onSubmit={handleSubmit}>
            <label style={styles.label}>State Name *</label>
            <input
              style={styles.input}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Enter state name"
            />

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
              <input
                id="state_active"
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked ? 1 : 0 }))}
              />
              <label htmlFor="state_active" style={{ fontSize: 13, color: "#0f172a" }}>
                Active
              </label>
            </div>

            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button style={styles.btnPrimary} type="submit" disabled={loading}>
                {isEditing ? "Update State" : "Add State"}
              </button>

              {isEditing && (
                <button type="button" style={styles.btnGhost} onClick={resetForm} disabled={loading}>
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        {/* SEARCH + LIST */}
        <div style={styles.card}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input
              style={{ ...styles.input, maxWidth: 280 }}
              placeholder="Search state..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <select
              style={{ ...styles.input, maxWidth: 170 }}
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>

            <button style={styles.btnGhost} type="button" onClick={() => loadData({ search, active: activeFilter })}>
              Search
            </button>

            <button
              style={styles.btnGhost}
              type="button"
              onClick={() => {
                setSearch("");
                setActiveFilter("");
                loadData({ search: "", active: "" });
              }}
            >
              Reset
            </button>
          </div>

          {loading ? (
            <div style={{ padding: 12 }}>Loading...</div>
          ) : err ? (
            <div style={{ color: "#b91c1c", marginTop: 10 }}>{err}</div>
          ) : !items.length ? (
            <div style={{ padding: 12, color: "#64748b" }}>No states found</div>
          ) : (
            <div style={{ marginTop: 12, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={{ padding: 10, textAlign: "left" }}>State Name</th>
                    <th style={{ padding: 10, textAlign: "left" }}>Status</th>
                    <th style={{ padding: 10, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const active = item.is_active !== 0;
                    return (
                      <tr key={item.id} style={{ borderTop: "1px solid #e2e8f0" }}>
                        <td style={{ padding: 10 }}>{item.name}</td>
                        <td style={{ padding: 10 }}>
                          <span style={styles.pill(active)}>{active ? "Active" : "Inactive"}</span>
                        </td>
                        <td style={{ padding: 10, textAlign: "right", whiteSpace: "nowrap" }}>
                          <button
                            type="button"
                            onClick={() => handleEdit(item)}
                            style={{ ...styles.btnGhost, padding: "4px 12px", marginRight: 6, fontSize: 13 }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(item.id)}
                            style={{
                              ...styles.btnGhost,
                              padding: "4px 12px",
                              background: "#fee2e2",
                              border: "1px solid #fecaca",
                              fontSize: 13,
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
                Note: State delete will fail if districts exist under the state.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
